import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import { BookingDataContext, BookingActionsContext } from "containers/data_context";
import { Accordion, Card, Overlay } from "react-bootstrap";

import RatePlanOccupancy from "./rate_plan_occupancy";
import RatePlanOccupancySelect from "./rate_plan_occupancy_select";
import RatePlanPolicies from "./rate_plan_policies";

import { useMemo } from "react";
import PolicySection from "./rate_plan_policies/policies_info/policies_breakdown/policy_section/policy_section";
import getPaymentPolicyText from "utils/get_payment_policy_text";
import getCancellationPorlicyText from "utils/get_cancellation_policy_text";
import styles from "./rate_plan.module.css";
import { useTranslation } from "react-i18next";

import Currency from "components/currency";

import RatePlanPriceBreakdown from "./rate_plan_price/rate_plan_price_breakdown/rate_plan_price_breakdown";
import { Row, Col } from "react-bootstrap";

import applySequentialDiscounts from "utils/applySequentialDiscount";
import buildPath from "utils/build_path";
import { useHistory } from "react-router-dom";
import routes from "routing/routes";
import classNames from "classnames";
import { useMedia } from "react-media";
import MEDIA_QUERIES from "constants/media_queries";
import CancellationBadge from "./rate_plan_policies/cancellation_badge";
import {
  ArrowRightOutlined,
  DoubleRightOutlined,
  RightOutlined,
  UpOutlined,
} from "@ant-design/icons";
const DEFAULT_AVAILABLE_SPACES = 0;

export default function RatePlan(props) {
  const { property, params } = useContext(BookingDataContext);

  const bookingData = useContext(BookingDataContext);
  const { channelId } = bookingData;
  const { checkinDate, checkoutDate } = params;
  const { saveDataToStorage } = useContext(BookingActionsContext);
  const {
    ratePlan,
    currency,
    disabled,
    occupiedSpaces,
    ratesOccupancy,

    disable,
    update,
    adultsOccupancy,
    childrenOccupancy,
    onOccupancyChange,
    bookedRoomOccupancy,
    promotions,
  } = props;
  const [availableSpaces, setAvailableSpaces] = useState(DEFAULT_AVAILABLE_SPACES);
  const { hotelPolicy = {} } = property.data;
  const { id, availability, cancellationPolicy, mealType } = ratePlan;

  const { totalPrice } = ratePlan;
  const { [id]: rateOccupancy = 0 } = ratesOccupancy;
  const { [id]: bookerRateOccupancy = 0 } = bookedRoomOccupancy;
  const { t } = useTranslation();
  const exclusiveTaxValue = () => {
    let totalTaxValue = 0;

    ratePlan.taxes.forEach((tax) => {
      if (!tax?.inclusive) {
        totalTaxValue += parseFloat(tax?.amount);
      }
    });
    return totalTaxValue;
  };

  const isAccommodationTaxIncluded = useMemo(() => {
    return ratePlan.taxes.find((tax) => tax?.title?.toLowerCase() === "accomodation tax")
      ?.inclusive;
  }, [ratePlan.taxes]);
  const totalExclusiveTax = exclusiveTaxValue();
  const priceAfterExclusiveTax = parseFloat(totalPrice) - totalExclusiveTax;
  const discountData = applySequentialDiscounts(priceAfterExclusiveTax, promotions);
  const [isPriceBreakdown, setIsPriceBreakdown] = useState(false);
  const target = useRef();
  const mobileTarget = useRef();
  const contRef = useRef();
  const [showTooltip, setShowTooltip] = useState(false);
  const history = useHistory();
  const matchedQueries = useMedia({ queries: MEDIA_QUERIES });
  const isMobile = matchedQueries.xs;

  const paymentPolicyText = useMemo(
    () => getPaymentPolicyText(cancellationPolicy),
    [cancellationPolicy, t("general:locale")],
  );
  const cancellationPolicyText = useMemo(
    () => getCancellationPorlicyText(cancellationPolicy),
    [cancellationPolicy, t("general:locale")],
  );
  const handleOccupancyChange = (updatedOccupancy) => {
    onOccupancyChange({ ...ratesOccupancy, [id]: updatedOccupancy });
  };
  const getHeightCallback = useCallback(() => {
    const cont = contRef.current;

    if (!cont) return 180; // If no element, return default height

    const elementHeight = cont?.clientHeight - 40; // Get the element height
    return elementHeight;
  }, [contRef?.current]);
  useEffect(() => {
    if (showTooltip) {
      setTimeout(() => {
        setShowTooltip(false);
      }, 3000);
    }
  }, [showTooltip]);
  const handleBook = useCallback(() => {
    saveDataToStorage(bookingData);

    const paymentPagePath = buildPath(history.location.search, routes.paymentPage, { channelId });

    history.push(paymentPagePath);
  }, [bookingData, saveDataToStorage, history, channelId]);

  useEffect(() => {
    const updatedAvailableSpaces = availability - occupiedSpaces;
    setAvailableSpaces(updatedAvailableSpaces);
  }, [occupiedSpaces, availability]);

  if (availableSpaces < 0) {
    return <></>;
  }

  return (
    <div ref={contRef}>
      {/* mobile structure */}
      <Row className={styles.mobileRowContainer}>
        <Col style={{ paddingRight: 0 }} xs={9} md={6} className={styles.leftSide}>
          <div className="d-flex w-100 justify-content-between mt-1">
            <RatePlanPolicies
              ratePlan={ratePlan}
              checkinDate={checkinDate}
              hotelPolicy={hotelPolicy}
            />

            <div ref={mobileTarget} className={styles.rightElement}>
              <RatePlanOccupancySelect
                optionsStyle={{ maxHeight: getHeightCallback() }}
                style={showTooltip && rateOccupancy === 0 ? { border: "1px solid red" } : {}}
                cancellationPolicy={cancellationPolicy}
                disabled={disabled}
                disable={disable}
                bookerRateOccupancy={bookerRateOccupancy}
                update={update}
                rateOccupancy={rateOccupancy}
                availableSpaces={availableSpaces}
                onChange={handleOccupancyChange}
                checkinDate={checkinDate}
              />
            </div>

            <Overlay target={mobileTarget.current} show={showTooltip} placement="top">
              {({ placement, arrowProps, show: _show, popper, ...props }) => (
                <div
                  {...props}
                  className={styles.tooltip}
                  style={{
                    ...props.style,
                  }}
                >
                  <div className={styles.tooltipArrow} />
                  {t("rates_table:select_room")}
                </div>
              )}
            </Overlay>
          </div>

          <div className={styles.detailInfo}>
            <div className="mt-2">
              <PolicySection title={t("rates_table:meals")} text={t(`meal_types:${mealType}`)} />
            </div>
            <div className="mt-2">
              {cancellationPolicyText && (
                <PolicySection
                  title={t("rates_table:cancellation")}
                  text={cancellationPolicyText}
                />
              )}
            </div>
            <div className="mt-2">
              {paymentPolicyText && (
                <PolicySection title={t("rates_table:prepayment")} text={paymentPolicyText} />
              )}
            </div>
          </div>
        </Col>
        <Col xs={3} md={6} style={{ paddingLeft: 0 }} className={styles.rightSide}>
          <div
            className={styles.bookBtn}
            onClick={() => {
              if (rateOccupancy === 0) {
                setShowTooltip(true);
              } else {
                handleBook();
              }
            }}
          >
            <div className={styles.btnPrice}>
              <Currency
                currency={currency}
                amount={promotions?.length > 0 ? discountData?.finalPrice : priceAfterExclusiveTax}
              />
            </div>
            {isAccommodationTaxIncluded ? (
              <span className={styles.taxText}>{t("rates_table:tax_included")}</span>
            ) : (
              <span className={styles.taxText}>{t("rates_table:tax_not_included")}</span>
            )}
            <DoubleRightOutlined className="my-2" color="white" />
            <span>{t("properties:book_now")}</span>
          </div>
          {/* </Tooltip> */}
        </Col>
      </Row>
      <Row className={styles.rowContainer}>
        <Col xs={8} md={6} className={styles.leftSide}>
          <div className="d-flex w-100">
            <RatePlanPolicies
              ratePlan={ratePlan}
              checkinDate={checkinDate}
              hotelPolicy={hotelPolicy}
            />
          </div>
        </Col>
        <Col xs={4} md={6} className={styles.rightSide}>
          <div className={styles.occupancy}>
            <RatePlanOccupancy
              occupancy={ratePlan.occupancy}
              adultsOccupancy={adultsOccupancy}
              childrenOccupancy={childrenOccupancy}
            />
          </div>

          <div className={styles.ratePlanPrice}>
            <div className="mt-1">
              <Currency
                currency={currency}
                amount={promotions?.length > 0 ? discountData?.finalPrice : priceAfterExclusiveTax}
              />
            </div>
          </div>

          <div ref={target} className={styles.rightElement}>
            <RatePlanOccupancySelect
              optionsStyle={{ maxHeight: getHeightCallback() }}
              style={showTooltip && rateOccupancy === 0 ? { border: "1px solid red" } : {}}
              cancellationPolicy={cancellationPolicy}
              disabled={disabled}
              disable={disable}
              bookerRateOccupancy={bookerRateOccupancy}
              update={update}
              rateOccupancy={rateOccupancy}
              availableSpaces={availableSpaces}
              onChange={handleOccupancyChange}
              checkinDate={checkinDate}
            />
          </div>
          {/* <Tooltip > */}

          <Overlay target={target.current} show={showTooltip} placement="top">
            {({ placement, arrowProps, show: _show, popper, ...props }) => (
              <div
                {...props}
                className={styles.tooltip}
                style={{
                  ...props.style,
                }}
              >
                <div className={styles.tooltipArrow} />
                {t("rates_table:select_room")}
              </div>
            )}
          </Overlay>

          <div
            className={styles.bookBtn}
            onClick={() => {
              if (rateOccupancy === 0) {
                setShowTooltip(true);
              } else {
                handleBook();
              }
            }}
          >
            {t("rates_table:book")}
            <div className={styles.btnPrice}>
              <Currency
                currency={currency}
                amount={promotions?.length > 0 ? discountData?.finalPrice : priceAfterExclusiveTax}
              />
            </div>
          </div>
          {/* </Tooltip> */}
        </Col>
      </Row>

      <Row md={12} className={styles.detailInfo}>
        <div className={`col-sm-6 col-xs-12 ${styles.mealInfo}`}>
          <div className="mt-2">
            <PolicySection title={t("rates_table:meals")} text={t(`meal_types:${mealType}`)} />
          </div>
          <div className="mt-2">
            {cancellationPolicyText && (
              <PolicySection title={t("rates_table:cancellation")} text={cancellationPolicyText} />
            )}
          </div>
          <div className="mt-2">
            {paymentPolicyText && (
              <PolicySection title={t("rates_table:prepayment")} text={paymentPolicyText} />
            )}
          </div>
        </div>
        <div className={classNames(styles.plandetails, "col-sm-6 col-xs-12")}>
          {isMobile ? (
            <></>
          ) : (
            <RatePlanPriceBreakdown
              ratePlan={ratePlan}
              currency={currency}
              discountData={discountData}
              totalPrice={priceAfterExclusiveTax}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
            />
          )}
        </div>
      </Row>
    </div>
  );
}
